<template>
  <div class="galaxy-map-bg">
    <div class="side-bg left-bg"></div>
    <div class="galaxy-map-wrapper">
      <!-- Sidebar Toolbox -->
      <transition name="slide">
        <div v-if="showSidebar" class="map-sidebar">
          <div class="sidebar-header">
            <span>Planet Search & Filter</span>
            <v-btn icon small @click="showSidebar = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
            <v-btn small color="primary" @click="resetFilters" style="min-width:0;padding:0 12px;">Reset Filters</v-btn>
            <span style="margin-left:auto;font-size:0.98em;color:#b6e2f3;font-weight:500;white-space:nowrap;min-width:120px;display:inline-block;text-align:right;">
              {{ filteredPlanets.length }} planet{{ filteredPlanets.length === 1 ? '' : 's' }} shown
            </span>
          </div>
          <div class="sidebar-content">
              <div class="filter-group compact-group">
                <label class="filter-label bold-label">Planet Name</label>
                <v-autocomplete
                  v-model="planetNameSearch"
                  :items="planetNameOptions"
                  label="Search by name"
                  dense
                  hide-details
                  clearable
                  solo
                  :menu-props="{ maxHeight: '200px' }"
                />
              </div>
                <div class="filter-group compact-group">
                  <label class="filter-label bold-label">Canon only</label>
                  <v-switch
                    v-model="canonOnlyToggle"
                    inset
                    dense
                    hide-details
                    color="primary"
                    :label="canonOnlyToggle ? 'Show only Canon planets' : 'Show all planets'"
                  />
                </div>
              <div class="filter-group compact-group">
                <label class="filter-label bold-label">Region</label>
                <v-autocomplete
                  v-model="regionFilter"
                  :items="regionOptions"
                  label="Select regions"
                  multiple
                  chips
                  dense
                  hide-details
                  clearable
                  solo
                  :menu-props="{ maxHeight: '200px' }"
                />
              </div>
              <div class="filter-group compact-group">
                <label class="filter-label bold-label">Appears In</label>
                <v-autocomplete
                  v-model="workFilter"
                  :items="workOptions"
                  label="Select works"
                  multiple
                  chips
                  dense
                  hide-details
                  clearable
                  solo
                  :menu-props="{ maxHeight: '300px' }"
                />
              </div>
              <div class="filter-group compact-group">
                <label class="filter-label bold-label">Diameter</label>
                <v-checkbox v-for="cat in diameterCategories" :key="cat" v-model="diameterFilter" :label="cat" :value="cat" hide-details dense class="compact-checkbox"></v-checkbox>
              </div>
              <div class="filter-group compact-group">
                <label class="filter-label bold-label">Population</label>
                <v-checkbox v-for="cat in populationCategories" :key="cat" v-model="populationFilter" :label="cat" :value="cat" hide-details dense class="compact-checkbox"></v-checkbox>
              </div>
          </div>
        </div>
      </transition>
      <v-btn v-if="!showSidebar" class="sidebar-toggle" icon small @click="showSidebar = true" style="position:absolute;top:16px;left:16px;z-index:10;"><v-icon>mdi-filter-variant</v-icon></v-btn>

      <div ref="mapEl" class="leaflet-map"></div>

      <!-- Planet Info Modal -->
      <v-dialog v-model="isModalOpen" max-width="720px">
        <v-card>
          <v-card-title class="d-flex flex-column">
            <div class="d-flex align-center justify-space-between" style="width:100%">
              <div>
                <div class="text-h5 planet-name-modal">{{ selectedPlanet?.name || 'Unknown' }}</div>
                <div class="text-h6 planet-name-modal" v-if="selectedPlanet?.location">({{ selectedPlanet.location }})</div>
              </div>
              <div class="badges">
                <span v-if="hasCanon(selectedPlanet)" class="badge canon">🌟 Canon</span>
                <span v-if="hasLegends(selectedPlanet)" class="badge legends">📜 Legends</span>
              </div>
            </div>
        </v-card-title>

        <v-card-text>
          <div v-if="selectedPlanet">
            <dl class="planet-dl">
              <dt>Region</dt>
              <dd>
                <template v-if="selectedPlanet.regions">
                  <span v-for="(region, i) in selectedPlanet.regions.split(';').map(r => r.trim()).filter(Boolean)" :key="i">
                    <template v-if="selectedPlanet.regions_URI && selectedPlanet.regions_URI.split(';')[i]">
                      <a :href="selectedPlanet.regions_URI.split(';')[i]" target="_blank" rel="noopener noreferrer">{{ region }}</a>
                    </template>
                    <template v-else>{{ region }}</template>
                    <span v-if="i < selectedPlanet.regions.split(';').length - 1">, </span>
                  </span>
                </template>
                <template v-else>{{ selectedPlanet.regions || 'Unknown' }}</template>
              </dd>

              <dt>Appears in</dt>
              <dd>
                <template v-if="worksList(selectedPlanet).length">
                  <span v-for="(w, i) in worksList(selectedPlanet)" :key="i">
                    <template v-if="w.uri">
                      <a :href="w.uri" target="_blank" rel="noopener noreferrer">{{ w.name }}</a>
                    </template>
                    <template v-else>{{ w.name }}</template>
                    <span v-if="i < worksList(selectedPlanet).length - 1">, </span>
                  </span>
                </template>
                <template v-else>Unknown</template>
              </dd>

              <dt>Population</dt><dd>{{ formatNumberWithCommas(selectedPlanet.population) }}</dd>
              <dt>Diameter</dt><dd>{{ formatValueWithUnit(selectedPlanet.diameter, selectedPlanet.diameter_unit) }}</dd>
              <dt>Orbital period</dt><dd>{{ formatValueWithUnit(selectedPlanet.orbital_period, selectedPlanet.orbital_period_unit) }}</dd>
              <dt>Rotation period</dt><dd>{{ formatValueWithUnit(selectedPlanet.rotation_period, selectedPlanet.rotation_period_unit) }}</dd>
            </dl>

            <div class="links-row">
              <a v-if="selectedPlanet.wookiepedia_url" :href="selectedPlanet.wookiepedia_url" target="_blank" rel="noopener noreferrer" class="modal-link-icon">
                <img :src="require('@/assets/wookiepedia_logo.webp')" alt="Wookieepedia" class="icon-link-img" />
                Wookieepedia
              </a>
              <br/>
              <a v-if="selectedPlanet.uri" :href="selectedPlanet.uri" target="_blank" rel="noopener noreferrer" class="modal-link-icon">
                <img :src="require('@/assets/wikidata_logo.png')" alt="Wikidata" class="icon-link-img" />
                Wikidata
              </a>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isModalOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
      </v-dialog>
    </div>
    <div class="side-bg right-bg"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps, computed } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ============================================================================
// PROPS & STATE
const canonOnlyToggle = ref(true);
// ============================================================================

const props = defineProps({
  planets: { type: Array, required: true }
});


const mapEl = ref(null);
const isModalOpen = ref(false);
const selectedPlanet = ref(null);
const showSidebar = ref(true);


const initialFilters = {
  canonLegends: 'canon',
  region: [],
  work: [],
  diameter: [],
  population: [],
  planetName: null
};
const canonLegendsFilter = ref(initialFilters.canonLegends);
const regionFilter = ref([...initialFilters.region]);
const regionOptions = [
  'Deep Core',
  'Core Worlds',
  'Colonies',
  'Inner Rim',
  'Expansion Region',
  'Mid Rim',
  'Outer Rim',
  'Unknown Regions',
  'Other'
];
const workFilter = ref([...initialFilters.work]);
const workOptions = computed(() => {
  // Collect all works from all planets, split by ';', trim, and deduplicate
  const allWorks = props.planets
    .map(p => p.works)
    .filter(Boolean)
    .flatMap(w => w.split(';').map(s => s.trim()))
    .filter(Boolean);
  return Array.from(new Set(allWorks)).sort((a, b) => a.localeCompare(b));
});
const diameterCategories = ["Small (<5,000 km)", "Medium (5,000-12,000 km)", "Large (>12,000 km)", "Unknown"];
const populationCategories = ["Low (<1M)", "Medium (1M-1B)", "High (>1B)", "Unknown"];
const diameterFilter = ref([]);
const populationFilter = ref([]);
const planetNameSearch = ref(initialFilters.planetName);
function resetFilters() {
  canonOnlyToggle.value = true;
  regionFilter.value = [...initialFilters.region];
  workFilter.value = [...initialFilters.work];
  diameterFilter.value = [];
  populationFilter.value = [];
  planetNameSearch.value = initialFilters.planetName;
}
const planetNameOptions = computed(() => {
  // Unique, sorted planet names from all planets
  const names = props.planets.map(p => p.name).filter(Boolean);
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
});

const canonLegendsFilterDisplay = computed({
  get() {
    if (canonLegendsFilter.value === 'canon') return 'Canon';
    if (canonLegendsFilter.value === 'legends') return 'Legends';
    return 'Both';
  },
  set(val) {
    if (val === 'Canon') canonLegendsFilter.value = 'canon';
    else if (val === 'Legends') canonLegendsFilter.value = 'legends';
    else canonLegendsFilter.value = 'both';
  }
});

let map = null;
let markerLayer = null;
let backgroundLayer = null;
let currentZoom = 1;
let lastGridFontSize = null;
let zoomTimeout = null;

// Map configuration constants
const GRID_CONFIG = { rows: 21, cols: 21, cellSize: 100 };
const MARKER_CONFIG = { radius: 8, gap: 4, attempts: 40 };

const REGION_COLORS = {
  'Deep Core': '#ff6c88',
  'Core Worlds': '#f4d26e',
  'Colonies': '#ddbad8',
  'Inner Rim': '#ffbf9c',
  'Expansion Region': '#b7d9f4',
  'Mid Rim': '#f3aeca',
  'Outer Rim': '#aeb8dc',
  'Unknown Regions': '#fbfcfe',
  'Other': '#73f7bb'
};

// ============================================================================
// UTILITY FUNCTIONS - COLORS
// ============================================================================


function getColorByRegion(region) {
  if (!region || typeof region !== 'string') return REGION_COLORS['Other'] || '#73f7bb';
  const regions = region.split(';').map(r => r.trim());
  for (const r of regions) {
    if (REGION_COLORS[r]) return REGION_COLORS[r];
  }
  return REGION_COLORS['Other'] || '#73f7bb';
}

function darkenColor(hex, percent = 30) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  
  const r = Math.max(0, (num >> 16) - amt);
  const g = Math.max(0, ((num >> 8) & 0xFF) - amt);
  const b = Math.max(0, (num & 0xFF) - amt);
  
  return '#' + [r, g, b]
    .map(c => Math.min(255, c).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

function getBorderStyle(planet, fillColor) {
  const isCanon = hasCanon(planet);
  const isLegends = hasLegends(planet);
  if (isCanon) return { color: darkenColor(fillColor, 35), weight: 3 };
  if (isLegends) return { color: fillColor, weight: 3 };
  return { color: '#999999', weight: 1 };
}

// ============================================================================
// UTILITY FUNCTIONS - LORE HELPERS
// ============================================================================

function hasCanon(planet) {
  return planet ? (planet.narrative_universes || '').toLowerCase().includes('star wars universe') : false;
}

function hasLegends(planet) {
  const narrative = (planet?.narrative_universes || '').toLowerCase();
  return narrative.includes('star wars legends') || narrative.includes('legends');
}

// ============================================================================
// UTILITY FUNCTIONS - DATA FORMATTING
// ============================================================================

function formatValueWithUnit(value, unit) {
  if (!value || !String(value).trim() || /^Unknown$/i.test(String(value))) return 'Unknown';
  // If value is a number, format with commas
  const formatted = !isNaN(Number(value)) ? Number(value).toLocaleString() : String(value);
  return unit && String(unit).trim() ? `${formatted} ${unit}` : formatted;
}

// Format numbers with commas, fallback to original if not a number
function formatNumberWithCommas(val) {
  if (val === undefined || val === null || val === '' || val === 'Unknown') return 'Unknown';
  const num = Number(val);
  return isNaN(num) ? val : num.toLocaleString();
}

function worksList(planet) {
  if (!planet) return [];
  
  const names = (planet.works || '').split(';').map(s => s.trim()).filter(Boolean);
  const uris = (planet.works_URI || '').split(';').map(s => s.trim()).filter(Boolean);
  
  if (!names.length && uris.length) {
    return uris.map(u => ({ name: decodeUriLabel(u), uri: u }));
  }
  
  return names.map((name, i) => ({ name, uri: uris[i] || null }));
}

function decodeUriLabel(uri) {
  try {
    return decodeURIComponent(uri.split('/').pop());
  } catch {
    return uri;
  }
}


// --- Dynamic marker and label size: tiny when zoomed out, readable when zoomed in ---
function getMarkerRadius(zoom) {
  return Math.max(1, Math.min(10, 1 + (zoom + 1) * 2));
}
function getLabelFontSize(zoom) {
  return Math.max(6, Math.min(14, 6 + (zoom + 1) * 2)) + 'px';
}
function getBorderWeight(zoom) {
  return Math.max(1, Math.min(2, 1 + Math.floor((zoom + 1) / 2)));
}


// --- Filtered planets as computed property ---
const filteredPlanets = computed(() => {
  return props.planets.filter(planet => matchesFilters(planet));
});

// Watch for planetNameSearch changes: if a planet is selected, center and open modal
watch(planetNameSearch, (val) => {
  if (!val) return;
  // Find the planet by name
  const planet = props.planets.find(p => p.name === val);
  if (planet && planet.cell_position && map) {
    // Center map on planet and zoom in to max level
    map.setView([planet.cell_position.y, planet.cell_position.x], 1, { animate: true });
    // Open modal
    selectedPlanet.value = planet;
    isModalOpen.value = true;
    // Clear all filters so all planets are visible
    canonLegendsFilter.value = 'both';
    regionFilter.value = [];
    workFilter.value = [];
    diameterFilter.value = [];
    populationFilter.value = [];
  }
});

function createMarker(latlng, zoom, fillColor, _borderStyle, planet) {
  const marker = L.circleMarker(latlng, {
    radius: getMarkerRadius(zoom),
    color: fillColor,
    fillColor,
    fillOpacity: 0.9,
    weight: 0
  });
  marker.bindPopup(`<strong>${planet.name}</strong>`);
  marker.on('click', () => {
    selectedPlanet.value = planet;
    isModalOpen.value = true;
  });
  marker.on('mouseover', () => {
    const currentZoom = map.getZoom();
    const baseRadius = getMarkerRadius(currentZoom);
    marker.setRadius(Math.min(baseRadius * 1.6, 24));
    marker.openPopup();
  });
  marker.on('mouseout', () => {
    const currentZoom = map.getZoom();
    marker.setRadius(getMarkerRadius(currentZoom));
    marker.closePopup();
  });
  return marker;
}

function createLabel(latlng, planetName, zoom) {
  const fontSize = getLabelFontSize(zoom);
  return L.tooltip(latlng, {
    permanent: true,
    direction: 'center',
    offset: [0, getMarkerRadius(zoom) + 7],
    className: 'planet-label',
    opacity: 1,
    content: `<span style="font-size:${fontSize}">${planetName}</span>`
  });
}

// ============================================================================
// DRAWING FUNCTIONS
// ============================================================================


function matchesFilters(planet) {
  // Canon only toggle
  if (canonOnlyToggle.value) {
    if (!hasCanon(planet)) return false;
  }
  // Region
  if (regionFilter.value.length > 0) {
    const regions = (planet.regions || '').split(';').map(r => r.trim());
    if (!regions.some(r => regionFilter.value.includes(r))) return false;
  }
  // Work
  if (workFilter.value.length > 0) {
    const works = (planet.works || '').split(';').map(w => w.trim());
    if (!works.some(w => workFilter.value.includes(w))) return false;
  }
  // Diameter (checkbox)
  if (diameterFilter.value.length > 0) {
    let cat = 'Unknown';
    if (planet.diameter && !isNaN(Number(planet.diameter))) {
      const d = Number(planet.diameter);
      if (d < 5000) cat = 'Small (<5,000 km)';
      else if (d <= 12000) cat = 'Medium (5,000-12,000 km)';
      else cat = 'Large (>12,000 km)';
    }
    if (!diameterFilter.value.includes(cat)) return false;
  }
  // Population (checkbox)
  if (populationFilter.value.length > 0) {
    let cat = 'Unknown';
    if (planet.population && !isNaN(Number(planet.population))) {
      const p = Number(planet.population);
      if (p < 1e6) cat = 'Low (<1M)';
      else if (p <= 1e9) cat = 'Medium (1M-1B)';
      else cat = 'High (>1B)';
    }
    if (!populationFilter.value.includes(cat)) return false;
  }
  // No filtering by planet name; search only recenters and opens modal
  return true;
}


function drawPlanets() {
  if (!markerLayer) markerLayer = L.layerGroup().addTo(map);
  markerLayer.clearLayers();
  if (!filteredPlanets.value.length) return;
  const zoom = map.getZoom();
  filteredPlanets.value.forEach(planet => {
    const pos = planet.cell_position;
    if (!pos) return;
    const latlng = [pos.y, pos.x];
    const fillColor = getColorByRegion(planet.regions);
    const marker = createMarker(latlng, zoom, fillColor, null, planet);
    const label = createLabel(latlng, planet.name, zoom);
    markerLayer.addLayer(marker);
    markerLayer.addLayer(label);
  });
}

// --- Performance: Use requestAnimationFrame for smooth marker/label updates ---
let pendingZoomUpdate = false;

// --- Performance: batch DOM updates for labels ---
function updateMarkerAndLabelSizes() {
  if (!markerLayer) return;
  if (pendingZoomUpdate) return;
  pendingZoomUpdate = true;
  requestAnimationFrame(() => {
    const zoom = map.getZoom();
    const labelUpdates = [];
    markerLayer.eachLayer(layer => {
      if (layer instanceof L.CircleMarker) {
        layer.setRadius(getMarkerRadius(zoom));
        layer.setStyle({ weight: getBorderWeight(zoom) });
      }
      if (layer instanceof L.Tooltip) {
        const el = layer.getElement();
        if (el && el.querySelector('span')) {
          labelUpdates.push({ el: el.querySelector('span'), size: getLabelFontSize(zoom) });
        }
      }
    });
    // Batch update label font sizes
    for (const upd of labelUpdates) {
      upd.el.style.fontSize = upd.size;
    }
    pendingZoomUpdate = false;
  });
}

function maybeRedrawGrid() {
  // Only redraw grid if label font size would change
  const zoom = map.getZoom();
  const fontSize = getLabelFontSize(zoom);
  if (fontSize !== lastGridFontSize) {
    drawGrid();
    lastGridFontSize = fontSize;
  }
}

function addBackgroundImage() {
  const { rows, cols, cellSize } = GRID_CONFIG;
  const bounds = [[0, 0], [rows * cellSize, cols * cellSize]];
  
  backgroundLayer = L.imageOverlay(
    require('@/assets/galaxy_background.jpg'),
    bounds,
    { opacity: 0.8, interactive: false, className: 'background-overlay' }
  );
  backgroundLayer.addTo(map);
}


// Removed unused labelTooltips and updateLabelVisibility

// --- Cleaned and optimized grid drawing ---
function drawGrid() {
  const { rows, cols, cellSize } = GRID_CONFIG;
  const GRID_COLOR = 'rgba(255,255,255,0.06)';
  // Remove previous grid layers if any
  if (map._gridLayer) {
    map.removeLayer(map._gridLayer);
  }
  const gridLayer = L.layerGroup();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const bounds = [
        [r * cellSize, c * cellSize],
        [(r + 1) * cellSize, (c + 1) * cellSize]
      ];
      gridLayer.addLayer(L.rectangle(bounds, { color: GRID_COLOR, weight: 1, fillOpacity: 0, interactive: false }));
      const letter = String.fromCharCode(65 + c);
      const number = rows - r;
      const label = `<span style="font-size:12px; color:rgba(255,255,255,0); text-shadow:0 0 1px #000;">${letter}-${number}</span>`;
      const center = [r * cellSize + cellSize / 2, c * cellSize + cellSize / 2];
      gridLayer.addLayer(L.tooltip(center, {
        permanent: true,
        direction: 'center',
        className: 'grid-cell-label-subtle',
        opacity: 0.08,
        content: label
      }));
    }
  }
  gridLayer.addTo(map);
  map._gridLayer = gridLayer;
}



// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

watch([
  () => props.planets,
  canonLegendsFilter,
  regionFilter,
  workFilter,
  diameterFilter,
  populationFilter,
  planetNameSearch,
  canonOnlyToggle
], () => {
  if (map) drawPlanets();
}, { immediate: false });

onMounted(() => {
  map = L.map(mapEl.value, {
    crs: L.CRS.Simple,
    minZoom: -1,
    maxZoom: 2,
    zoomControl: false
  });
  const { rows, cols, cellSize } = GRID_CONFIG;
  const bounds = [[0, 0], [rows * cellSize, cols * cellSize]];
  map.fitBounds(bounds);
  map.setZoom(1);
  map.setMaxBounds(bounds);
  map.on('zoomend', () => {
    if (zoomTimeout) clearTimeout(zoomTimeout);
    zoomTimeout = setTimeout(() => {
      currentZoom = map.getZoom();
      updateMarkerAndLabelSizes();
      maybeRedrawGrid();
    }, 60);
  });
  drawPlanets();
  drawGrid();
  addBackgroundImage();
});

onBeforeUnmount(() => {
  if (map) map.remove();
});
</script>

<style scoped>
/* Sidebar styles */
.map-sidebar {
  position: absolute;
  top: 0;
  left: 0;
  width: 320px;
  height: 100%;
  background: #121622 !important;
  box-shadow: 2px 0 16px 0 rgba(0,0,0,0.18);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 18px 16px 16px 16px;
  border-right: 2px solid #222;
  overflow-y: auto;
}
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.15em;
  font-weight: 700;
  color: #b6e2f3;
  margin-bottom: 12px;
}
.sidebar-content {
  flex: 1;
  overflow-y: auto;
  color: #b6e2f3;
}
.sidebar-toggle {
  background: rgba(18,22,34,0.85);
}
.filter-group {
  margin-bottom: 7px;
  padding-bottom: 2px;
}
.compact-group {
  margin-bottom: 4px !important;
  padding-bottom: 0 !important;
}
.filter-label {
  font-size: 0.98em;
  margin-bottom: 2px;
  color: #b6e2f3;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.bold-label {
  font-weight: 700;
  color: #e3f6ff;
}
.compact-checkbox {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s cubic-bezier(.4,0,.2,1);
}
.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');

:root {
  --footer-height: 56px;
}

@media (max-width: 600px) {
  :root {
    --footer-height: 72px;
  }
}

.galaxy-map-bg {
  display: flex;
  flex-direction: row;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #000;
  align-items: flex-start;
  justify-content: center;
  min-height: 0;
  height: calc(100vh - var(--footer-height, 56px));
  margin-top: 0;
}

.side-bg {
  flex: 1 1 0;
  min-width: 120px;
  height: 100vh;
  background-image: url('@/assets/bg_space_seamless.png');
  background-repeat: repeat;
  background-size: cover;
  background-position: center;
  z-index: 1;
}

.left-bg {
  border-right: 2px solid #222;
}
.right-bg {
  border-left: 2px solid #222;
}

.galaxy-map-wrapper {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  /* Use a very wide 16:7 aspect ratio, maximize width but fit above footer */
  width: min(100vw, calc((100vh - var(--footer-height, 56px)) * 16 / 7));
  height: min(calc(100vw * 7 / 16), calc(100vh - var(--footer-height, 56px)));
  aspect-ratio: 16 / 7;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.leaflet-map {
  flex: 1;
  border: 1px solid #1e2a33;
  position: relative;
  background-color: #000000;
  width: 100%;
  height: 100%;
  aspect-ratio: 4 / 3;
}

/* Modal / badges / description list styling */
.badges { display:flex; gap:8px; align-items:center; }
.badge { padding:6px 10px; border-radius:999px; color:#fff; font-weight:600; font-size:.85rem; display:inline-flex; align-items:center; gap:6px;}
.badge.canon { background: #1ea64a; }
.badge.legends { background: #f08f2f; }

.planet-dl { display:grid; grid-template-columns: 140px 1fr; gap:8px 12px; margin:0 0 12px 0; }
.planet-dl dt { color:#9aa6b2; font-size:.85rem; margin:0; }
.planet-dl dd { margin:0; font-size:.95rem; }

.links-row { margin-top:8px; }
.links-row a { color:#0b66f0; text-decoration:underline; margin-right:8px; }

.planet-name-modal {
  font-family: 'Orbitron', sans-serif;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-size: 2.5rem;
  line-height: 1.1;
}

/* Planet name labels on the map */
:deep(.planet-label) {
  background: none;
  border: none;
  box-shadow: none;
  font-size: 0.75rem;
  color: #b6e2f3;
  font-weight: 600;
  text-shadow: 0 0 3px rgba(0,0,0,0.7);
  white-space: nowrap;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 0.5px;
}

:deep(.planet-label-inner) {
  background: none;
  padding: 0;
}

.grid-cell-label-subtle {
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-shadow: none;
  pointer-events: none;
  font-family: 'Segoe UI', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}


.v-main {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

/* Add to <style scoped> for triangle removal */
.planet-label .leaflet-tooltip-arrow {
  display: none !important;
}

.icon-link-img {
  width: 48px;
  height: 48px;
  vertical-align: middle;
  margin-right: 4px;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.10);
  background: #fff;
  object-fit: contain;
}
.modal-link-icon {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 500;
  text-decoration: none;
  color: #4fc3f7;
  margin-right: 8px;
  transition: color 0.15s;
}
.modal-link-icon:hover {
  color: #0288d1;
}
</style>